import { PUBLIC_LABEL_LOCAL_ASSETS, PUBLIC_LABEL_LOCAL_ASSETS_V2 } from '@sqi/shared-schemas';
export { PUBLIC_LABEL_LOCAL_ASSETS, PUBLIC_LABEL_LOCAL_ASSETS_V2 };
export const LOCAL_OCR_ASSET_PATH = '/ocr/tesseract-7.0.0-v2';
export interface LocalOcrProgress {
  stage: string;
  progress: number;
}
export interface LocalOcrResult {
  rawText: string;
  confidence: number;
}
interface Packet {
  jobId?: string;
  status?: string;
  data?: unknown;
}
let active: { cancel: () => void } | null = null;
export function localLabelOcrBusy() {
  return active !== null;
}
/** Own the native v7 worker before its first load: initialization is hard-cancellable too.
 * No createWorker wrapper, descendants, CDN defaults, cache, image URL, or logging. */
export async function readLocalLabel(
  prepared: { png: string; hash: string; engineAssets: string },
  signal: AbortSignal,
  onProgress: (value: LocalOcrProgress) => void,
  options: { timeoutMs?: number; workerFactory?: (url: string) => Worker } = {},
): Promise<LocalOcrResult> {
  if (active) throw Error('local_ocr_busy');
  signal.throwIfAborted();
  // Only a server-bound, known descriptor selects code/models. Never accept URLs.
  const version =
    prepared.engineAssets === PUBLIC_LABEL_LOCAL_ASSETS
      ? 'v1'
      : prepared.engineAssets === PUBLIC_LABEL_LOCAL_ASSETS_V2
        ? 'v2'
        : null;
  if (!version) throw Error('unsupported_local_engine');
  const path = new URL('/ocr/tesseract-7.0.0-' + version + '/', location.origin);
  const worker = options.workerFactory
    ? options.workerFactory(path.href + 'worker.min.js')
    : new Worker(path.href + 'worker.min.js');
  let done = false;
  let rejectPending: ((reason: Error) => void) | null = null;
  let expectedJob = '';
  let resolvePending: ((value: unknown) => void) | null = null;
  const cancel = () => {
    if (done) return;
    done = true;
    worker.terminate();
    rejectPending?.(new Error('local_ocr_cancelled_or_timeout'));
  };
  const owner = { cancel };
  active = owner;
  const timer = setTimeout(cancel, options.timeoutMs ?? 120000);
  signal.addEventListener('abort', cancel, { once: true });
  worker.onerror = (event) => {
    event.preventDefault();
    cancel();
  };
  worker.onmessage = (event: MessageEvent<Packet>) => {
    if (done || signal.aborted || event.data.jobId !== expectedJob) return;
    const { status, data } = event.data;
    if (status === 'progress') {
      const v = data as { status?: unknown; progress?: unknown };
      const stage =
        v.status === 'recognizing text' ? 'กำลังอ่านฉลาก' : 'กำลังเตรียมเครื่องมือภาษาไทยและอังกฤษ';
      onProgress({
        stage,
        progress: typeof v.progress === 'number' ? Math.max(0, Math.min(1, v.progress)) : 0,
      });
    } else if (status === 'resolve') {
      resolvePending?.(data);
    } else if (status === 'reject') {
      cancel();
    }
  };
  const send = (action: string, payload: unknown): Promise<unknown> => {
    if (done || signal.aborted) return Promise.reject(new Error('local_ocr_cancelled'));
    expectedJob = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      resolvePending = resolve;
      rejectPending = reject;
      worker.postMessage({ workerId: 'sqi-local-label', jobId: expectedJob, action, payload });
    });
  };
  try {
    const image = Uint8Array.from(atob(prepared.png), (v) => v.charCodeAt(0));
    if (image.length < 24 || image.length > 2097152) throw Error('invalid_local_pixels');
    const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', image)), (v) =>
      v.toString(16).padStart(2, '0'),
    ).join('');
    if (digest !== prepared.hash) throw Error('local_pixel_hash_mismatch');
    signal.throwIfAborted();
    await send('load', {
      options: {
        corePath: path.href + (version === 'v2' ? 'core/tesseract-core-lstm.wasm.js' : 'core'),
        lstmOnly: true,
        logging: false,
      },
    });
    await send('loadLanguage', {
      langs: 'tha+eng',
      options: { langPath: path.href + 'lang', gzip: true, cacheMethod: 'none', lstmOnly: true },
    });
    await send('initialize', { langs: 'tha+eng', oem: 1, config: { user_defined_dpi: '300' } });
    const value = (await send('recognize', {
      image,
      options: { rotateAuto: true, tessedit_pageseg_mode: '3' },
      output: { text: true, blocks: false, hocr: false, tsv: false },
    })) as { text?: unknown; confidence?: unknown };
    if (done || signal.aborted) throw Error('local_ocr_cancelled');
    const rawText = typeof value.text === 'string' ? value.text : '';
    const confidence =
      typeof value.confidence === 'number' && Number.isFinite(value.confidence)
        ? Math.max(0, Math.min(100, value.confidence))
        : 0;
    return {
      rawText: rawText.slice(0, 4000),
      confidence: rawText.length > 4000 ? Math.min(confidence, 64) : confidence,
    };
  } finally {
    done = true;
    worker.terminate();
    clearTimeout(timer);
    signal.removeEventListener('abort', cancel);
    worker.onmessage = null;
    worker.onerror = null;
    if (active === owner) active = null;
    resolvePending = null;
    rejectPending = null;
  }
}
